import {
  DEFAULT_DOCUMENT_EVIDENCE_LABEL,
  type DocumentEvidenceReference,
} from "@repo/shared/document-evidence-link";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

export const DOCUMENT_EVIDENCE_NODE_NAME = "documentEvidence";

export { DEFAULT_DOCUMENT_EVIDENCE_LABEL };

/**
 * One citation as the interaction layer hands it around: the locator plus the label to
 * show until the authorized presentation answers with the real document name.
 */
export interface DocumentEvidenceCitation {
  reference: DocumentEvidenceReference;
  fallbackLabel: string;
}

/**
 * One or more `documentEvidence` nodes that sit next to each other in the same parent,
 * separated by nothing but whitespace. Readers see such a group as a single citation
 * marker, so grouping is decided here once and reused by the decoration plugin (which
 * hides the continuations) and by the click handler (which lists the whole run).
 */
export interface EvidenceRun {
  /** Document positions of each node in the run, in document order. */
  positions: number[];
  nodes: ProseMirrorNode[];
}

/**
 * A run whose whole textblock is nothing but citations, together with the place its marker
 * belongs instead. Claudia routinely authors the citations for a passage as a paragraph of
 * their own, which with compact markers renders as a lone icon on an otherwise empty line.
 * Such a run is *hoisted*: the paragraph is hidden and the marker is drawn at the end of the
 * preceding line of text. The stored document is never touched — this is rendering only.
 */
export interface HoistableEvidenceRun {
  run: EvidenceRun;
  /** Start of the citation-only textblock, which is hidden where it sits. */
  blockFrom: number;
  /** End of that textblock. */
  blockTo: number;
  /** End of the preceding textblock's content: where the proxy marker is drawn. */
  hostPos: number;
}

export function isDocumentEvidenceNode(node: ProseMirrorNode): boolean {
  return node.type.name === DOCUMENT_EVIDENCE_NODE_NAME;
}

/**
 * Diff rendering marks inserted/removed citations with `_diffStatus`. Those must stay
 * individually visible and labeled, so they never join a run — a reviewer has to see
 * exactly which citations a revision added or removed.
 */
function isGroupableEvidenceNode(node: ProseMirrorNode): boolean {
  return isDocumentEvidenceNode(node) && !node.attrs._diffStatus;
}

function isWhitespaceOnlyText(node: ProseMirrorNode): boolean {
  return node.isText && (node.text ?? "").trim() === "";
}

function collectRunsInParent(
  parent: ProseMirrorNode,
  contentStart: number,
  runs: EvidenceRun[]
): void {
  const open: EvidenceRun[] = [];

  parent.forEach((child, childOffset) => {
    if (isGroupableEvidenceNode(child)) {
      const pos = contentStart + childOffset;
      const current = open[0];
      if (current) {
        current.positions.push(pos);
        current.nodes.push(child);
      } else {
        open.push({ positions: [pos], nodes: [child] });
      }
      return;
    }
    // Whitespace between two citations is authoring noise, not a separator — but it also
    // never opens a run on its own, so a leading/trailing space changes nothing.
    if (isWhitespaceOnlyText(child)) return;
    runs.push(...open.splice(0));
  });

  runs.push(...open.splice(0));
}

/**
 * Every run in the document, including runs of one, in document order.
 */
export function collectEvidenceRuns(doc: ProseMirrorNode): EvidenceRun[] {
  const runs: EvidenceRun[] = [];

  const walk = (node: ProseMirrorNode, contentStart: number) => {
    if (node.inlineContent) {
      collectRunsInParent(node, contentStart, runs);
      return;
    }
    node.forEach((child, childOffset) => {
      // `contentStart + childOffset` is the child's own position; its content starts one
      // token later, past the child's opening token.
      walk(child, contentStart + childOffset + 1);
    });
  };

  walk(doc, 0);
  return runs;
}

/**
 * The run containing the evidence node that starts at `pos`, or `null` when the position
 * is not the start of a groupable evidence node (a diff-marked citation, for instance).
 */
export function findEvidenceRunAtPos(doc: ProseMirrorNode, pos: number): EvidenceRun | null {
  return collectEvidenceRuns(doc).find((run) => run.positions.includes(pos)) ?? null;
}

/**
 * A textblock holding citations and nothing else (whitespace between them is authoring
 * noise). A diff-marked node anywhere in it disqualifies the block: diff review must show
 * every added or removed citation exactly where the revision put it.
 */
function isEvidenceOnlyTextblock(node: ProseMirrorNode): boolean {
  if (!node.isTextblock || node.attrs._diffStatus) return false;

  let hasEvidence = false;
  let onlyEvidence = true;
  node.forEach((child) => {
    if (!onlyEvidence) return;
    if (isGroupableEvidenceNode(child)) {
      hasEvidence = true;
      return;
    }
    if (isWhitespaceOnlyText(child)) return;
    onlyEvidence = false;
  });

  return onlyEvidence && hasEvidence;
}

type HostCandidate = { status: "host"; pos: number } | { status: "rejected" };

/**
 * The last textblock inside `node` that can host a hoisted marker, searched backwards.
 * `null` means "nothing here, keep looking further back"; `rejected` means the nearest
 * thing that *would* have hosted refuses to, so the run stays where it is.
 */
function findHostInNode(node: ProseMirrorNode, pos: number): HostCandidate | null {
  if (node.isTextblock) {
    // Two citation paragraphs in a row belong to the same line of text, so the first one
    // is skipped rather than used as a host — both end up on the same line.
    if (isEvidenceOnlyTextblock(node)) return null;
    // A heading titles what follows it; a citation for the text below must not land in it.
    if (node.type.name === "heading") return { status: "rejected" };
    return { status: "host", pos: pos + 1 + node.content.size };
  }

  // An isolating node (a rich section, a table cell) is its own field of text. Reaching
  // into the previous one would attach a citation to prose it does not belong to.
  if (node.type.spec.isolating) return { status: "rejected" };

  const children: { child: ProseMirrorNode; offset: number }[] = [];
  node.forEach((child, offset) => {
    children.push({ child, offset });
  });
  for (let index = children.length - 1; index >= 0; index -= 1) {
    const entry = children[index];
    if (!entry) continue;
    // A citation paragraph after a list belongs to that list's last line, so descend.
    const found = findHostInNode(entry.child, pos + 1 + entry.offset);
    if (found) return found;
  }
  return null;
}

function findHostPos(parent: ProseMirrorNode, index: number, blockPos: number): number | null {
  // Siblings are contiguous, so each one ends where the next begins — walking back from the
  // citation block's own position avoids having to know where the parent's content starts.
  let siblingEnd = blockPos;
  for (let siblingIndex = index - 1; siblingIndex >= 0; siblingIndex -= 1) {
    const sibling = parent.child(siblingIndex);
    const siblingPos = siblingEnd - sibling.nodeSize;
    siblingEnd = siblingPos;
    const found = findHostInNode(sibling, siblingPos);
    if (found) return found.status === "host" ? found.pos : null;
  }
  return null;
}

/**
 * Every citation-only textblock that has somewhere to hoist to, in document order. A block
 * with no preceding text (the first block of a field, or one that only follows a heading)
 * is absent: it renders where it is stored.
 */
export function collectHoistableEvidenceRuns(doc: ProseMirrorNode): HoistableEvidenceRun[] {
  const hoistable: HoistableEvidenceRun[] = [];

  doc.descendants((node, pos, parent, index) => {
    if (!node.isTextblock) return true;
    if (!parent || !isEvidenceOnlyTextblock(node)) return false;

    const hostPos = findHostPos(parent, index, pos);
    if (hostPos === null) return false;

    // A citation-only block breaks on nothing, so it holds exactly one run.
    const runs: EvidenceRun[] = [];
    collectRunsInParent(node, pos + 1, runs);
    const run = runs[0];
    if (run) {
      hoistable.push({ run, blockFrom: pos, blockTo: pos + node.nodeSize, hostPos });
    }
    return false;
  });

  return hoistable;
}

function readTrimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function readEvidenceLabelFromAttrs(attrs: Record<string, unknown>): string {
  return readTrimmedString(attrs.label) || DEFAULT_DOCUMENT_EVIDENCE_LABEL;
}

/**
 * A node whose locator is incomplete, or whose span is present but nonsensical, is not a
 * citation at all — the same rule the DOM reader applies to `data-*` attributes.
 */
export function readEvidenceReferenceFromAttrs(
  attrs: Record<string, unknown>
): DocumentEvidenceReference | null {
  const artifactId = readTrimmedString(attrs.artifactId);
  const processingRevisionId = readTrimmedString(attrs.processingRevisionId);
  const blockId = readTrimmedString(attrs.blockId);
  if (!artifactId || !processingRevisionId || !blockId) return null;

  const startChar = typeof attrs.startChar === "number" ? attrs.startChar : null;
  const endChar = typeof attrs.endChar === "number" ? attrs.endChar : null;
  const hasSpan =
    startChar !== null &&
    endChar !== null &&
    Number.isSafeInteger(startChar) &&
    Number.isSafeInteger(endChar) &&
    startChar >= 0 &&
    startChar < endChar;
  if (!hasSpan && (startChar !== null || endChar !== null)) return null;

  return {
    kind: "document_block",
    artifactId,
    processingRevisionId,
    blockId,
    ...(hasSpan ? { span: { startChar, endChar } } : {}),
  };
}

/**
 * Identity of a cited passage, for comparing two references without deep-equality.
 */
export function buildEvidenceReferenceKey(reference: DocumentEvidenceReference): string {
  return [
    reference.artifactId,
    reference.processingRevisionId,
    reference.blockId,
    reference.span?.startChar ?? "",
    reference.span?.endChar ?? "",
  ].join("|");
}

/** Every usable citation in a run, in document order. */
export function readEvidenceRunCitations(run: EvidenceRun): DocumentEvidenceCitation[] {
  return run.nodes.flatMap((node) => {
    const reference = readEvidenceReferenceFromAttrs(node.attrs);
    return reference ? [{ reference, fallbackLabel: readEvidenceLabelFromAttrs(node.attrs) }] : [];
  });
}
